import axios from 'axios'

// API Configuration
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://api.corebriefing.com/api/v1'
  : 'http://localhost:3001/api/v1';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor for auth tokens
api.interceptors.request.use(
  (config) => {
    // Add auth token when available
    const token = localStorage.getItem('auth_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Only redirect for authentication endpoints, not admin functions
      const isAuthEndpoint = error.config?.url?.includes('/auth/') || error.config?.url?.includes('/profile')
      
      if (isAuthEndpoint) {
        // Handle unauthorized access
        localStorage.removeItem('auth_token')
        window.location.href = '/login'
      }
      // For admin endpoints, let the component handle the error
    }
    return Promise.reject(error)
  }
)

export interface HealthResponse {
  status: string
  timestamp: string
  uptime: number
  environment: string
}

export interface StatusResponse {
  message: string
  version: string
  environment: string
}

export interface User {
  id: number
  email: string
  firstName: string
  lastName: string
  role: string
  isActive: boolean
  createdAt: string
  lastLogin?: string
}

export interface AuthResponse {
  message: string
  user: User
  token: string
}

export interface LoginCredentials {
  email: string
  password: string
}

export interface RegisterData {
  email: string
  password: string
  firstName: string
  lastName: string
  role?: string
}

export interface MacroData {
  id: string
  title: string
  value: number
  change: number
  changePercent: number
  timestamp: string
  category: 'inflation' | 'employment' | 'gdp' | 'markets' | 'rates'
}

// FRED Admin Interface Types
export interface FredSeries {
  id: string
  title: string
  category: string
}

export interface FredAgent {
  name: string
  type: string
  schedule: string
  dataSource: string
  enabled: boolean
  createdAt: string
  updatedAt: string
}

export interface FredStatistics {
  totalExecutions: number
  successfulExecutions: number
  failedExecutions: number
  avgExecutionTime: number
  lastExecution: string | null
}

export interface FredConfig {
  agent: FredAgent
  apiKey: {
    status: string
    configured: boolean
  }
  series: {
    total: number
    list: FredSeries[]
  }
  statistics: FredStatistics
}

export interface FredDataPoint {
  series_id: string
  series_title: string
  category: string
  value: number
  date: string
  units: string
  frequency: string
  source: string
  last_updated: string
}

// Yahoo Finance interfaces
export interface YahooSymbol {
  symbol: string
  name: string
  category: string
}

export interface YahooAgent {
  name: string
  type: string
  schedule: string
  dataSource: string
  enabled: boolean
  createdAt: string
  updatedAt: string
}

export interface YahooConfig {
  agent: YahooAgent
  symbols: {
    total: number
    list: YahooSymbol[]
  }
  statistics: {
    totalExecutions: number
    successfulExecutions: number
    failedExecutions: number
    avgExecutionTime: number
    lastExecution: string | null
  }
  dataStats: {
    totalRecords: number
    symbolsCount: number
    dataRange: {
      earliest: string | null
      latest: string | null
    }
    lastExecution: string | null
  }
}

export interface YahooDataPoint {
  symbol: string
  symbol_name: string
  category: string
  open_price: number
  high_price: number
  low_price: number
  close_price: number
  volume: number
  date: string
  source: string
  last_updated: string
}

// News Spider interfaces
export interface NewsSpider {
  id: string
  name: string
  description: string
  icon: string
  categories: string[]
  type: 'energy' | 'financial' | 'general' | 'transportation'
  premium: boolean
  enabled: boolean
  maxArticles: number
  defaultMaxArticles: number
  processingTime: string
  features: string[]
  selectedCategories?: string[]
  schedule?: string
  lastExecution?: string
  supportsCustomTickers?: boolean
  customTickerPrefix?: string
  customTickers?: string[]
  statistics: {
    totalExecutions: number
    successfulExecutions: number
    failedExecutions: number
    avgExecutionTime: number
    lastExecution: string | null
  }
}

export interface NewsSpiderConfig {
  spiders: NewsSpider[]
  totalSpiders: number
  enabledSpiders: number
  spiderTypes: string[]
  apiEndpoint: string
}

export interface NewsSpiderExecution {
  id: number
  spider_id: string
  status: 'pending' | 'running' | 'success' | 'failed'
  triggered_by: number
  trigger_type: 'manual' | 'scheduled' | 'bulk'
  config: any
  job_id: string
  articles_scraped: number
  execution_time_ms: number
  error_message: string | null
  response_data: any
  created_at: string
  updated_at: string
  triggered_by_email: string
  triggered_by_name: string
}

export interface NewsSpiderExecutionHistory {
  executions: NewsSpiderExecution[]
  total: number
  limit: number
  offset: number
}

// User Invitation interfaces
export interface UserInvitation {
  id: number
  email: string
  role: string
  firstName?: string
  lastName?: string
  expiresAt: string
  acceptedAt?: string
  createdAt: string
  isExpired: boolean
  status: 'pending' | 'expired' | 'accepted'
  invitedBy?: {
    email: string
    name: string
  }
}

export interface InvitationData {
  email: string
  role: string
  firstName?: string
  lastName?: string
}

export interface InvitationResponse {
  success: boolean
  message: string
  data: UserInvitation
}

export interface InvitationsListResponse {
  success: boolean
  data: {
    invitations: UserInvitation[]
    pagination: {
      currentPage: number
      totalPages: number
      totalInvitations: number
      limit: number
    }
  }
}

export interface InvitationDetails {
  email: string
  role: string
  firstName?: string
  lastName?: string
  expiresAt: string
  invitedBy: {
    name: string
    email: string
  }
}

export interface AcceptInvitationData {
  token: string
  password: string
  firstName?: string
  lastName?: string
}

export const ApiService = {
  // Health check
  async getHealth(): Promise<HealthResponse> {
    const response = await api.get('/health')
    return response.data
  },

  // API status
  async getStatus(): Promise<StatusResponse> {
    const response = await api.get('/status')
    return response.data
  },

  // Authentication
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await api.post('/auth/login', credentials)
    return response.data
  },

  async register(userData: RegisterData): Promise<AuthResponse> {
    const response = await api.post('/auth/register', userData)
    return response.data
  },

  async getProfile(): Promise<User> {
    const response = await api.get('/auth/profile')
    return response.data.user
  },

  async getDebug() {
    const response = await api.get('/auth/debug')
    return response.data
  },

  // ============================================================================
  // USER INVITATION MANAGEMENT
  // ============================================================================

  async sendInvitation(invitationData: InvitationData): Promise<InvitationResponse> {
    const response = await api.post('/admin/users/invite', invitationData)
    return response.data
  },

  async getInvitations(page = 1, limit = 20, status = 'pending'): Promise<InvitationsListResponse> {
    const response = await api.get('/admin/users/invitations', {
      params: { page, limit, status }
    })
    return response.data
  },

  async cancelInvitation(invitationId: number): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/admin/users/invitations/${invitationId}`)
    return response.data
  },

  async resendInvitation(invitationId: number): Promise<{ success: boolean; message: string }> {
    const response = await api.post(`/admin/users/invitations/${invitationId}/resend`)
    return response.data
  },

  async cleanupExpiredInvitations(): Promise<{ success: boolean; message: string; data: { expiredCount: number } }> {
    const response = await api.post('/admin/users/invitations/cleanup')
    return response.data
  },

  // Public invitation acceptance endpoints
  async validateInvitation(token: string): Promise<{ success: boolean; data: InvitationDetails }> {
    const response = await api.get(`/auth/invitation/${token}`)
    return response.data
  },

  async acceptInvitation(acceptData: AcceptInvitationData): Promise<AuthResponse> {
    const response = await api.post('/auth/accept-invitation', acceptData)
    return response.data
  },

  // ============================================================================
  // USER MANAGEMENT API
  // ============================================================================

  async getUsers(params?: { page?: number; limit?: number; role?: string; isActive?: string }): Promise<{
    users: User[]
    pagination: {
      currentPage: number
      totalPages: number
      totalUsers: number
      limit: number
    }
  }> {
    const response = await api.get('/admin/users', { params })
    return response.data
  },

  async getUserStats(): Promise<{
    totalUsers: number
    activeUsers: number
    inactiveUsers: number
    adminUsers: number
    recentRegistrations: number
    recentLogins: number
    roleDistribution: { [key: string]: number }
  }> {
    const response = await api.get('/admin/users/stats')
    return response.data
  },

  async toggleUserStatus(userId: number): Promise<{ message: string; user: User }> {
    const response = await api.put(`/admin/users/${userId}/toggle-status`)
    return response.data
  },

  async updateUserRole(userId: number, role: string): Promise<{ message: string; user: User }> {
    const response = await api.put(`/admin/users/${userId}/role`, { role })
    return response.data
  },

  // Mock data for now - will be replaced with real endpoints
  async getMacroData(): Promise<MacroData[]> {
    // Mock data until backend endpoints are built
    return [
      {
        id: '1',
        title: 'CPI Inflation Rate',
        value: 3.2,
        change: 0.1,
        changePercent: 3.2,
        timestamp: new Date().toISOString(),
        category: 'inflation'
      },
      {
        id: '2',
        title: 'Unemployment Rate',
        value: 3.7,
        change: -0.1,
        changePercent: -2.6,
        timestamp: new Date().toISOString(),
        category: 'employment'
      },
      {
        id: '3',
        title: 'GDP Growth (QoQ)',
        value: 2.1,
        change: 0.3,
        changePercent: 16.7,
        timestamp: new Date().toISOString(),
        category: 'gdp'
      },
      {
        id: '4',
        title: 'S&P 500',
        value: 4756.50,
        change: 23.45,
        changePercent: 0.5,
        timestamp: new Date().toISOString(),
        category: 'markets'
      },
      {
        id: '5',
        title: 'Fed Funds Rate',
        value: 5.25,
        change: 0,
        changePercent: 0,
        timestamp: new Date().toISOString(),
        category: 'rates'
      }
    ]
  },

  // ============================================================================
  // FRED ADMIN MANAGEMENT API
  // ============================================================================

  /**
   * Get FRED configuration (Admin only)
   */
  async getFredConfig(): Promise<FredConfig> {
    const response = await api.get('/admin/fred/config')
    return response.data.data
  },

  /**
   * Update FRED schedule (Admin only)
   */
  async updateFredSchedule(schedule: string): Promise<void> {
    await api.put('/admin/fred/schedule', { schedule })
  },

  /**
   * Enable/disable FRED agent (Admin only)
   */
  async updateFredStatus(enabled: boolean): Promise<void> {
    await api.put('/admin/fred/status', { enabled })
  },

  /**
   * Add new FRED series (Admin only)
   */
  async addFredSeries(series: { id: string; title: string; category: string }): Promise<void> {
    await api.post('/admin/fred/series', series)
  },

  /**
   * Remove FRED series (Admin only)
   */
  async removeFredSeries(seriesId: string): Promise<void> {
    await api.delete(`/admin/fred/series/${seriesId}`)
  },

  /**
   * Get latest FRED data (Admin only)
   */
  async getFredLatestData(): Promise<Record<string, FredDataPoint[]>> {
    const response = await api.get('/admin/fred/data/latest')
    return response.data.data.byCategory
  },

  /**
   * Test FRED API connection and get sample data
   */
  async testFredConnection(): Promise<any> {
    const response = await api.get('/admin/fred/test')
    return response.data
  },

  /**
   * Trigger FRED historical backfill (Admin only, password protected)
   */
  async triggerFredBackfill(password: string): Promise<any> {
    // Use shorter timeout since we respond immediately now
    const response = await api.post('/admin/fred/backfill', { password }, {
      timeout: 30000 // 30 seconds timeout since we respond immediately
    })
    return response.data
  },

  /**
   * Get FRED backfill status (Admin only)
   */
  async getFredBackfillStatus(): Promise<any> {
    const response = await api.get('/admin/fred/backfill/status')
    return response.data.data
  },

  // ============================================================================
  // YAHOO FINANCE ADMIN MANAGEMENT API
  // ============================================================================

  /**
   * Get Yahoo Finance configuration (Admin only)
   */
  async getYahooConfig(): Promise<YahooConfig> {
    const response = await api.get('/admin/yahoo/config')
    return response.data.data
  },

  /**
   * Update Yahoo Finance schedule (Admin only)
   */
  async updateYahooSchedule(schedule: string): Promise<void> {
    await api.put('/admin/yahoo/schedule', { schedule })
  },

  /**
   * Enable/disable Yahoo Finance agent (Admin only)
   */
  async updateYahooStatus(enabled: boolean): Promise<void> {
    await api.put('/admin/yahoo/status', { enabled })
  },

  /**
   * Add new Yahoo Finance symbol (Admin only)
   */
  async addYahooSymbol(symbol: { symbol: string; name: string; category: string }): Promise<void> {
    await api.post('/admin/yahoo/symbols', symbol)
  },

  /**
   * Remove Yahoo Finance symbol (Admin only)
   */
  async removeYahooSymbol(symbol: string): Promise<void> {
    await api.delete(`/admin/yahoo/symbols/${symbol}`)
  },

  /**
   * Get latest Yahoo Finance data (Admin only)
   */
  async getYahooLatestData(): Promise<Record<string, YahooDataPoint[]>> {
    const response = await api.get('/admin/yahoo/data/latest')
    return response.data.data.byCategory
  },

  /**
   * Get Yahoo Finance public config (no auth required)
   */
  async getYahooPublicConfig(): Promise<YahooConfig> {
    const response = await api.get('/yahoo/config')
    return response.data.data
  },

  /**
   * Initialize Yahoo Finance agent (Admin only)
   */
  async initializeYahooAgent(): Promise<any> {
    const response = await api.post('/admin/yahoo/init')
    return response.data
  },

  /**
   * Trigger Yahoo Finance historical backfill (Admin only, password protected)
   */
  async triggerYahooBackfill(password: string): Promise<any> {
    // Use shorter timeout since we respond immediately now
    const response = await api.post('/admin/yahoo/backfill', { password }, {
      timeout: 30000 // 30 seconds timeout since we respond immediately
    })
    return response.data
  },

  /**
   * Get Yahoo Finance backfill status
   */
  async getYahooBackfillStatus(): Promise<any> {
    const response = await api.get('/admin/yahoo/backfill/status')
    return response.data
  },

  /**
   * Legacy Scraper API Methods
   */
  
  /**
   * Trigger legacy scraper (McCloskey/SOFR Quant Scraper)
   */
  async triggerLegacyScraper(password: string): Promise<any> {
    const response = await api.post('/admin/legacy-scraper/trigger', { password }, {
      timeout: 30000 // 30 seconds timeout since it responds immediately
    })
    return response.data
  },

  /**
   * Get legacy scraper configuration and status
   */
  async getLegacyScraperStatus(): Promise<any> {
    const response = await api.get('/admin/legacy-scraper/status')
    return response.data
  },



  /**
   * Get legacy scraper S3 outputs for a specific date
   */
  async getLegacyScraperOutputs(date?: string): Promise<any> {
    const params = date ? { date } : {}
    const response = await api.get('/admin/legacy-scraper/outputs', { params })
    return response.data
  },

  /**
   * Get legacy scraper schedule configuration
   */
  async getLegacyScraperSchedule(): Promise<any> {
    const response = await api.get('/admin/legacy-scraper/schedule')
    return response.data
  },

  /**
   * Update legacy scraper schedule configuration
   */
  async updateLegacyScraperSchedule(schedule: string, enabled: boolean): Promise<any> {
    const response = await api.post('/admin/legacy-scraper/schedule', { schedule, enabled })
    return response.data
  },

  /**
   * Run spider diagnostic
   */
  async runSpiderDiagnostic(): Promise<any> {
    const response = await api.get('/admin/diagnostic/spiders')
    return response.data
  },

  // ============================================================================
  // NEWS SPIDERS ADMIN MANAGEMENT API
  // ============================================================================

  /**
   * Get news spiders configuration (Admin only)
   */
  async getNewsSpidersConfig(): Promise<NewsSpiderConfig> {
    const response = await api.get('/admin/news-spiders/config')
    return response.data.data
  },

  /**
   * Update spider configuration (Admin only)
   */
  async updateSpiderConfig(spiderId: string, config: {
    enabled: boolean
    maxArticles: number
    selectedCategories: string[]
    schedule?: string
    customTickers?: string[]
  }): Promise<void> {
    await api.put(`/admin/news-spiders/${spiderId}/config`, config)
  },

  /**
   * Trigger a specific spider manually (Admin only, password protected)
   */
  async triggerSpider(spiderId: string, password: string, config: {
    maxArticles: number
    selectedCategories: string[]
    customTickers?: string[]
  }): Promise<any> {
    const response = await api.post(`/admin/news-spiders/${spiderId}/trigger`, {
      password,
      ...config
    }, {
      timeout: 300000 // 5 minutes timeout
    })
    return response.data
  },

  /**
   * Trigger multiple spiders at once (Admin only, password protected)
   */
  async triggerBulkSpiders(password: string, spiders: Array<{
    id: string
    maxArticles: number
    selectedCategories: string[]
    customTickers?: string[]
  }>): Promise<any> {
    const response = await api.post('/admin/news-spiders/bulk-trigger', {
      password,
      spiders
    }, {
      timeout: 600000 // 10 minutes timeout for bulk
    })
    return response.data
  },

  /**
   * Get spider execution history (Admin only)
   */
  async getSpiderExecutions(spiderId?: string, limit = 50, offset = 0): Promise<NewsSpiderExecutionHistory> {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString()
    })
    
    if (spiderId) {
      params.append('spiderId', spiderId)
    }
    
    const response = await api.get(`/admin/news-spiders/executions?${params}`)
    return response.data.data
  },

  /**
   * Update article count for latest spider execution (Admin only)
   */
  async updateSpiderArticleCount(spiderId: string): Promise<any> {
    const response = await api.post(`/admin/news-spiders/${spiderId}/update-article-count`)
    return response.data
  },

  // ============================================================================
  // DASHBOARD API METHODS
  // ============================================================================

  /**
   * Get comprehensive dashboard overview for executives
   */
  async getDashboardOverview(): Promise<DashboardOverview> {
    const response = await api.get('/dashboard/overview')
    return response.data.data
  },

  /**
   * Get news intelligence analysis
   */
  async getNewsIntelligence(timeframe = '24h', categories?: string[], sources?: string[]): Promise<NewsIntelligence> {
    const params = new URLSearchParams()
    params.append('timeframe', timeframe)
    if (categories?.length) params.append('categories', categories.join(','))
    if (sources?.length) params.append('sources', sources.join(','))
    
    const response = await api.get(`/dashboard/news-intelligence?${params}`)
    return response.data.data
  },

  /**
   * Get market analysis data
   */
  async getMarketAnalysis(): Promise<MarketAnalysis> {
    const response = await api.get('/dashboard/market-analysis')
    return response.data.data
  },

  /**
   * Get industry-specific insights
   */
  async getIndustryInsights(industry: 'coal' | 'steel' | 'transportation' | 'energy'): Promise<IndustrySpecificData> {
    const response = await api.get(`/dashboard/industry/${industry}`)
    return response.data.data
  }
}

// ============================================================================
// DASHBOARD INTERFACE DEFINITIONS
// ============================================================================

export interface DashboardOverview {
  newsIntelligence: NewsIntelligence;
  marketAnalysis: MarketAnalysis;
  economicIndicators?: {
    indicators: EconomicIndicator[];
    lastUpdated: string;
  };
  commodityPrices: CommodityPrices;
  aiInsights?: AIInsights;
  timestamp: string;
}

export interface NewsIntelligence {
  timeframe: string
  totalArticles: number
  avgSentiment: number
  keyTopics: { word: string; count: number }[]
  priorityArticles: NewsArticle[]
  sourceBreakdown: { source: string; article_count: number; avg_sentiment: number }[]
  categoryBreakdown: { category: string; article_count: number; avg_sentiment: number }[]
  latestArticles: NewsArticle[]
  lastUpdated: string
}

export interface NewsArticle {
  id: number
  title: string
  content?: string
  summary?: string
  url: string
  author?: string
  published_at: string
  scraped_at: string
  source: string
  category?: string
  tags?: string[]
  sentiment_score?: number
  relevanceScore?: number
  word_count?: number
}

export interface MarketAnalysis {
  keyMetrics: {
    cnr?: MarketDataPoint;
    sp500?: MarketDataPoint;
    hrcSteel?: MarketDataPoint;
    api2Coal?: {
      name: string;
      price: string | number;
      source: string;
    };
    treasuryYield?: {
      name: string;
      price: string | number;
      source: string;
    };
    newsArticles?: {
      count: number;
      timeframe: string;
    };
  };
  naturalResourcesStocks: MarketDataPoint[];
  otherStocks: MarketDataPoint[];
  commodities: MarketDataPoint[];
  lastUpdated: string;
}

export interface EconomicIndicator {
  series_id: string
  series_title: string
  category: string
  value: number
  date: string
  units?: string
  frequency?: string
}

export interface MarketDataPoint {
  symbol: string
  symbol_name: string
  category: string
  close_price: number
  open_price?: number
  high_price?: number
  low_price?: number
  volume?: number
  date: string
  // Percent changes for different time periods
  day_change_percent?: number
  week_change_percent?: number
  month_change_percent?: number
  ytd_change_percent?: number
}

export interface MarketChange {
  symbol: string
  current_price: number
  current_date: string
  daily_price?: number
  weekly_price?: number
  monthly_price?: number
  ytd_price?: number
  day_change_percent: number
  week_change_percent?: number
  month_change_percent?: number
  ytd_change_percent?: number
}

export interface EconomicIndicators {
  indicators: EconomicIndicatorWithTrend[]
  categories: {
    inflation: EconomicIndicatorWithTrend[]
    employment: EconomicIndicatorWithTrend[]
    gdp: EconomicIndicatorWithTrend[]
    rates: EconomicIndicatorWithTrend[]
  }
  lastUpdated: string
}

export interface EconomicIndicatorWithTrend {
  series_id: string
  series_title: string
  category: string
  current_value: number
  current_date: string
  historical_value?: number
  change_percent_3m?: number
}

export interface CommodityPrice {
  name: string
  latest: string | number
  previous?: string | number
  daily_change?: number
  weekly_change?: number
  mtd_change?: number
  ytd_change?: number
  source: string
  category: string
  status?: string // 'unavailable' if data not available
}

export interface CommodityPrices {
  categories: {
    coal: CommodityPrice[];
    steel: CommodityPrice[];
    yield: CommodityPrice[];
    bonds: BondInfo[];
  };
  status: string;
  dataDate: string | null;
  message: string;
  lastUpdated: string;
}

export interface BondInfo {
  id: string;
  name: string;
  coupon: string;
  maturity_date: string;
  principal_amount: string;
  latest_trade_date: string;
  latest_price_range: string;
  latest_trade_count: string;
  latest_trade_amount: string;
  timestamp: string;
  status: 'available' | 'unavailable';
}

export interface IndustryInsights {
  coal: IndustryData
  steel: IndustryData
  transportation: IndustryData
  energy: IndustryData
  lastUpdated: string
}

export interface IndustryData {
  articles: NewsArticle[]
  sentiment: number
  trends: string
}

export interface IndustrySpecificData {
  industry: string
  articles: NewsArticle[]
  marketData: MarketDataPoint[]
  sentiment: number
  articleCount: number
  topSources: { source: string; count: number }[]
  keyThemes: { word: string; count: number }[]
  lastUpdated: string
}

export interface AIInsights {
  executiveSummary: string
  keyInsights: AIInsight[]
  recommendations: string[]
  riskFactors: string[]
  opportunities: string[]
  confidence: number
  lastUpdated: string
}

export interface AIInsight {
  category: string
  insight: string
  confidence: number
  impact: 'bullish' | 'bearish' | 'caution' | 'stable' | 'favorable' | 'mixed'
}

export default api 